import type * as api from '@gitbook/api-client';
import { type NavigationScreenState, resolveURLToNavigation } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import type { SidebarTreeItemValue } from '@gitbook/spine-react';
import { assert, assertNever } from '@gitbook/util-ts';

import {
    SiteContentIcon,
    SiteSectionGroupIcon,
    SiteSectionIcon,
    SpaceIcon,
} from '../../Components';
import type { NavigationTarget } from '../../navigation';
import { getSiteSectionsFromSiteSectionGroup } from './structure';

export type AnySiteItem = SiteItem | SiteSectionItem | SiteSectionGroupItem | SiteSpaceItem;

interface SiteItem extends SidebarTreeItemValue {
    type: 'site';
    site: api.Site;
    href: NavigationTarget;
    isActive: (screen: NavigationScreenState) => boolean;
    children: SiteStructureItem[];
}

interface SiteTreeContext {
    hideEmptySectionGroups?: boolean;
    expandableSiteSectionIds?: string[];
}

/**
 * Get a site item from a site and its structure.
 */
export function getSiteTreeItem(
    input: {
        site: api.Site;
        structure: api.SiteStructure;
    },
    context?: SiteTreeContext
): SiteItem {
    const { site, structure } = input;
    const screen = resolveURLToNavigation(site.urls.app);
    assert(screen, 'Impossible to resolve site URL');
    return {
        id: site.id,
        type: 'site',
        title: site.title,
        icon: <SiteContentIcon site={site} />,
        site,
        href: { screen },
        isActive: (screen) => screen.type === 'site' && screen.site === site.id,
        children: getSiteStructureItems(structure, context ?? {}),
    };
}

type SiteStructureItem = SiteSectionItem | SiteSectionGroupItem | SiteSpaceItem;

function getSiteStructureItems(
    structure: api.SiteStructure,
    context: SiteTreeContext
): SiteStructureItem[] {
    switch (structure.type) {
        case 'sections': {
            const singleSiteSpace =
                structure.structure.length === 1 &&
                structure.structure[0]?.object === 'site-section' &&
                structure.structure[0].siteSpaces.length === 1
                    ? structure.structure[0].siteSpaces[0]
                    : undefined;

            if (singleSiteSpace) {
                return getSingleSiteSpaceStructure(singleSiteSpace);
            }

            return structure.structure
                .map((item) => {
                    switch (item.object) {
                        case 'site-section':
                            return getSiteSectionItem(item, context);
                        case 'site-section-group': {
                            if (
                                context.hideEmptySectionGroups &&
                                getSiteSectionsFromSiteSectionGroup(item).length === 0
                            ) {
                                return null;
                            }
                            return getSiteSectionGroupItem(item, context);
                        }
                        default:
                            assertNever(item);
                    }
                })
                .filter((item) => item !== null);
        }
        case 'siteSpaces': {
            const singleSiteSpace =
                structure.structure.length === 1 ? structure.structure[0] : undefined;

            if (singleSiteSpace) {
                return getSingleSiteSpaceStructure(singleSiteSpace);
            }

            return structure.structure.map(getSiteSpaceItem);
        }
        default:
            assertNever(structure);
    }
}

interface SiteSectionGroupItem extends SidebarTreeItemValue {
    type: 'site-section-group';
    siteSectionGroup: api.SiteSectionGroup;
    children: Array<SiteSectionGroupItem | SiteSectionItem>;
}

function getSiteSectionGroupItem(
    siteSectionGroup: api.SiteSectionGroup,
    context: SiteTreeContext
): SiteSectionGroupItem {
    return {
        id: siteSectionGroup.id,
        type: 'site-section-group',
        title: siteSectionGroup.title,
        icon: <SiteSectionGroupIcon siteSectionGroup={siteSectionGroup} />,
        siteSectionGroup,
        children: siteSectionGroup.children.map((child) =>
            child.object === 'site-section-group'
                ? getSiteSectionGroupItem(child, context)
                : getSiteSectionItem(child, context)
        ),
    };
}

interface SiteSectionItem extends SidebarTreeItemValue {
    type: 'site-section';
    siteSection: api.SiteSection;
    href: NavigationTarget | undefined;
    isActive: ((screen: NavigationScreenState) => boolean) | undefined;
    children: SiteSpaceItem[];
    space: api.Space | null;
    url: string | null;
}

function getSiteSectionItem(
    siteSection: api.SiteSection,
    context: SiteTreeContext
): SiteSectionItem {
    const singleSiteSpace =
        siteSection.siteSpaces.length === 1 ? siteSection.siteSpaces[0] : undefined;
    const isExpandable = context?.expandableSiteSectionIds?.includes(siteSection.id);
    return {
        id: siteSection.id,
        type: 'site-section',
        title: siteSection.title,
        icon: <SiteSectionIcon siteSection={siteSection} />,
        siteSection,
        href: singleSiteSpace ? getSiteSpaceHref(singleSiteSpace) : undefined,
        isActive: singleSiteSpace ? getSiteSpaceIsActive(singleSiteSpace) : undefined,
        children:
            isExpandable !== undefined
                ? isExpandable
                    ? siteSection.siteSpaces.map((space) => getSiteSpaceItem(space))
                    : []
                : singleSiteSpace
                  ? []
                  : siteSection.siteSpaces.map((space) => getSiteSpaceItem(space)),
        space: singleSiteSpace ? singleSiteSpace.space : null,
        url: siteSection.urls.published || null,
    };
}

interface SiteSpaceItem extends SidebarTreeItemValue {
    type: 'site-space';
    siteSpace: api.SiteSpace;
    href: NavigationTarget;
    isActive: (screen: NavigationScreenState) => boolean;
    space: api.Space;
    url: string | null;
}

function getSiteSpaceItem(siteSpace: api.SiteSpace): SiteSpaceItem {
    return {
        id: siteSpace.id,
        type: 'site-space',
        title: siteSpace.title,
        siteSpace,
        href: getSiteSpaceHref(siteSpace),
        isActive: getSiteSpaceIsActive(siteSpace),
        icon: Icon.Documentation,
        space: siteSpace.space,
        url: siteSpace.urls.published || null,
    };
}

/**
 * Get the whole structure when there is only one site space.
 * Meaning the site has only one content in the structure.
 */
function getSingleSiteSpaceStructure(singleSiteSpace: api.SiteSpace) {
    return [
        {
            ...getSiteSpaceItem(singleSiteSpace),
            icon: Icon.SpaceSymlink,
            title: 'Space',
        },
    ];
}

function getSiteSpaceHref(siteSpace: api.SiteSpace): NavigationTarget {
    const screen = resolveURLToNavigation(siteSpace.space.urls.app);
    assert(screen !== null, 'Site section screen should be defined');
    return { screen };
}

function getSiteSpaceIsActive(siteSpace: api.SiteSpace) {
    return (screen: NavigationScreenState) =>
        screen.type === 'space' && screen.space === siteSpace.space.id;
}
export type ContentItem = SpaceItem | CollectionItem;

export function getContentItems(
    contents: (api.OrganizationAllCollection | api.OrganizationAllSpace)[]
): ContentItem[] {
    const spaces: SpaceItem[] = [];
    const collections: CollectionItem[] = [];
    for (const content of contents) {
        if ('collection' in content) {
            collections.push(getCollectionItem(content));
        } else {
            spaces.push(getSpaceItem(content));
        }
    }
    return [...spaces, ...collections];
}

interface SpaceItem extends SidebarTreeItemValue {
    type: 'space';
    space: api.OrganizationAllSpace;
    href: NavigationTarget;
    isActive: (screen: NavigationScreenState) => boolean;
}

function getSpaceItem(space: api.OrganizationAllSpace): SpaceItem {
    return {
        id: space.space.id,
        type: 'space',
        title: space.space.title,
        space: space,
        href: getSpaceHref(space.space),
        isActive: getSpaceIsActive(space.space),
        icon: <SpaceIcon space={space.space} />,
    };
}

export interface CollectionItem extends SidebarTreeItemValue {
    type: 'collection';
    collection: api.OrganizationAllCollection;
    children: ContentItem[];
}

function getCollectionItem(collection: api.OrganizationAllCollection): CollectionItem {
    return {
        id: collection.collection.id,
        type: 'collection',
        title: collection.collection.title,
        collection,
        icon: Icon.Collection,
        children: getContentItems(collection.content),
    };
}

function getSpaceHref(space: api.Space): NavigationTarget {
    const screen = resolveURLToNavigation(space.urls.app);
    assert(screen !== null, 'Space screen should be defined');
    return { screen };
}

function getSpaceIsActive(space: api.Space) {
    return (screen: NavigationScreenState) => screen.type === 'space' && screen.space === space.id;
}
